import color from 'src/theme/color';

export enum TaskStatus {
    NEW = 1,
    PENDING = 2,
    PAUSED = 3,
    IN_PROCESS = 4,
    SOLVED = 5,
    ACCEPTED = 6,
    CANCELLED = 7,
    INVALID = 8,
    PENDING_CLASS_PARAMS = 9,
    PENDING_PACKAGE = 10,
    MODIFICATIONS_REQUESTED = 11,
    ON_HOLD = 12
}

export const TaskStatusMap: Record<TaskStatus, string> = {
    [TaskStatus.NEW]: 'Pending',
    [TaskStatus.PENDING]: 'Pending',
    [TaskStatus.PAUSED]: 'Paused',
    [TaskStatus.IN_PROCESS]: 'In Progress',
    [TaskStatus.SOLVED]: 'Solved',
    [TaskStatus.ACCEPTED]: 'Solved',
    [TaskStatus.CANCELLED]: 'Cancelled',
    [TaskStatus.INVALID]: 'Invalid',
    [TaskStatus.PENDING_CLASS_PARAMS]: 'Pending Class Params',
    [TaskStatus.PENDING_PACKAGE]: 'Pending Package',
    [TaskStatus.MODIFICATIONS_REQUESTED]: 'Modifications Requested',
    [TaskStatus.ON_HOLD]: 'On Hold'
};

export function getTaskStatusColors(status: TaskStatus): {
    bg: string;
    textColor: string;
} {
    switch (status) {
        case TaskStatus.ACCEPTED:
        case TaskStatus.SOLVED:
            return {
                bg: color.darkGreenBlack,
                textColor: color.brightGreen
            };
        case TaskStatus.CANCELLED:
        case TaskStatus.INVALID:
            return { bg: color.darkGreyBlack, textColor: color.lightRed };
        case TaskStatus.IN_PROCESS:
            return { bg: color.darkGreyBlack, textColor: color.chineseGold };
        case TaskStatus.NEW:
        case TaskStatus.PENDING:
            return { bg: color.darkGreyBlack, textColor: color.turquoise };
        case TaskStatus.ON_HOLD:
            return {
                bg: color.darkGreyBlack,
                textColor: color.greenBlue
            };
        default:
            return { bg: color.darkGreyBlack, textColor: color.lightPink };
    }
}

export enum TaskType {
    CREATE_FUNCTION = 1,
    UPDATE_FUNCTION = 2,
    DESCRIBE_FUNCTION = 3,
    REVIEW_TASK = 4,
    OPEN_TASK = 5,
    CREATE_REACT_COMPONENT = 6,
    UPDATE_REACT_COMPONENT = 7,
    CHECK_REUSABILITY = 8,
    CODING = 9,
    QUEST_REVIEW = 13,
    IMPLEMENTATION_PLAN = 14,
    QUEST_BREAKDOWN = 15,
    REVIEW_REQUEST_REJECTION = 16,
    PLANNING_QUESTIONS_ANSWERING = 17,
    AI_QUEST_REVIEW = 18
}

export interface TaskDetail {
    id: string;
    title: string;
    description: string;
    status: TaskStatus;
    taskType: TaskType;
    questId: string | null;
    iteration: number | null;
    created: string;
    functionName?: string;
    feedback?: string;
    reviewStatus?: number;
    reviewFeedback?: string;
    reviewCompleted?: boolean;
    llmCostUsd?: number;
    contributorCostUsd?: number;
    solutionUrls?: string[];
}

export interface TaskActivity {
    id: number;
    user: string;
    activityType: number;
    details: Record<string, any>;
    created: string;
}

export interface TaskSummary {
    id: string;
    title: string;
    status: TaskStatus;
    taskType: TaskType;
    iteration: number | null;
    created: string;
}

export function getTaskStatusLabel(status: TaskStatus): string {
    switch (status) {
        case TaskStatus.NEW:
            return 'Pending';
        case TaskStatus.PENDING:
            return 'Pending';
        case TaskStatus.PAUSED:
            return 'Paused';
        case TaskStatus.IN_PROCESS:
            return 'In Progress';
        case TaskStatus.SOLVED:
            return 'Solved';
        case TaskStatus.ACCEPTED:
            return 'Solved';
        case TaskStatus.CANCELLED:
            return 'Cancelled';
        case TaskStatus.INVALID:
            return 'Invalid';
        case TaskStatus.PENDING_CLASS_PARAMS:
            return 'Pending Class Params';
        case TaskStatus.PENDING_PACKAGE:
            return 'Pending Package';
        case TaskStatus.MODIFICATIONS_REQUESTED:
            return 'Modifications Requested';
        case TaskStatus.ON_HOLD:
            return 'On Hold';
        default:
            return String(status);
    }
}

export function getTaskTypeLabel(taskType: TaskType): string {
    switch (taskType) {
        case TaskType.CREATE_FUNCTION:
            return 'Create Function';
        case TaskType.UPDATE_FUNCTION:
            return 'Update Function';
        case TaskType.DESCRIBE_FUNCTION:
            return 'Describe Function';
        case TaskType.REVIEW_TASK:
            return 'Review Task';
        case TaskType.OPEN_TASK:
            return 'Open Task';
        case TaskType.CREATE_REACT_COMPONENT:
            return 'Create React Component';
        case TaskType.UPDATE_REACT_COMPONENT:
            return 'Update React Component';
        case TaskType.CHECK_REUSABILITY:
            return 'Check Reusability';
        case TaskType.CODING:
            return 'Coding';
        case TaskType.QUEST_REVIEW:
            return 'Quest Review';
        case TaskType.AI_QUEST_REVIEW:
            return 'AI Quest Review';
        case TaskType.IMPLEMENTATION_PLAN:
            return 'Implementation Plan';
        case TaskType.QUEST_BREAKDOWN:
            return 'Quest Breakdown';
        case TaskType.REVIEW_REQUEST_REJECTION:
            return 'Review Request Rejection';
        case TaskType.PLANNING_QUESTIONS_ANSWERING:
            return 'Planning Questions Answering';
        default:
            return String(taskType);
    }
}
